import { z } from 'zod';
import { PERMISSION_PRIMITIVES } from '../rbac/primitives.js';
/**
 * RBAC & User Management contracts (spec §13.1).
 *
 * Until this existed, the only way to grant somebody a role was to re-seed the
 * database — which meant the one authority QMS360 rests on was also the one
 * thing no administrator could change, and the correction path was exactly what
 * Rule 9 forbids.
 *
 * ADR-0005 is the constraint running through everything below: **a custom role
 * is composed from the permission primitives and nothing else.** That is what
 * keeps a role somebody invents checkable by the same engine as the seven
 * shipped ones, and it is why the permission map is a closed enum here rather
 * than a free-form object.
 */
export const grantScopeSchema = z.enum(['none', 'own', 'scoped', 'all']);
export const grantSubjectSchema = z.enum(['master_data', 'admin_roles', 'car', 'engagement']);
export const grantSchema = z.object({
    scope: grantScopeSchema,
    /** "Masters", "CAR only", "Admin only" — a restriction on *what*, not how far. */
    only: grantSubjectSchema.optional(),
});
/**
 * A permission set: primitives to grants, and only the primitives that exist.
 *
 * A typo in a key would otherwise create a grant the engine never reads — a role
 * that looks like it confers something and confers nothing, which is the failure
 * shape this project keeps finding.
 */
export const permissionSetSchema = z.record(z.enum(PERMISSION_PRIMITIVES), grantSchema);
export const APPS = ['qaqc', 'lesson-learned', 'audit'];
// ---------------------------------------------------------------------------
// Workspace role grants
// ---------------------------------------------------------------------------
/**
 * Granting somebody a Workspace Role, with the scope that grant reaches.
 *
 * `expiresAt` is what makes the External/Guest Auditor time-boxed (ADR-0005);
 * the engine treats a past expiry as no grant at all, re-checked on every
 * request rather than trusted from a token (ADR-0015).
 */
/**
 * A date the organisation means (DATA-13): a calendar date "YYYY-MM-DD", which
 * the server reads in the organisation's timezone — an expiry ends as that day
 * ends there — or an exact instant with its offset.
 */
const dateOrInstant = z.union([
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date (YYYY-MM-DD) or a date and time.'),
    z.string().datetime({ offset: true }),
]);
export const workspaceGrantSchema = z.object({
    userId: z.string().uuid(),
    workspaceRoleId: z.string().uuid(),
    businessUnitIds: z.array(z.string().uuid()).max(200).optional(),
    projectIds: z.array(z.string().uuid()).max(500).optional(),
    engagementIds: z.array(z.string().uuid()).max(500).optional(),
    expiresAt: dateOrInstant.nullable().optional(),
});
export const scopeUpdateSchema = z.object({
    businessUnitIds: z.array(z.string().uuid()).max(200).optional(),
    projectIds: z.array(z.string().uuid()).max(500).optional(),
    engagementIds: z.array(z.string().uuid()).max(500).optional(),
    expiresAt: dateOrInstant.nullable().optional(),
});
/** A new account (spec §13.1): a Guest Auditor, or anyone who signs in locally. */
export const userCreateSchema = z.object({
    username: z
        .string()
        .trim()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9._-]+$/i, 'Letters, numbers, dots, dashes and underscores only.'),
    email: z.string().trim().toLowerCase().email(),
    displayName: z.string().trim().min(1).max(160),
    designation: z.string().trim().max(160).nullable().optional(),
    platformRoleId: z.string().uuid(),
});
export const platformRoleAssignmentSchema = z.object({
    platformRoleId: z.string().uuid().nullable(),
});
// ---------------------------------------------------------------------------
// The role builder (ADR-0005)
// ---------------------------------------------------------------------------
export const workspaceRoleCreateSchema = z.object({
    app: z.enum(APPS),
    label: z.string().trim().min(1, 'Give the role a name.').max(200),
    description: z.string().trim().max(2000).nullable().optional(),
    permissions: permissionSetSchema,
});
export const rolePermissionsUpdateSchema = z.object({
    permissions: permissionSetSchema,
});
// ---------------------------------------------------------------------------
// Delegation register
// ---------------------------------------------------------------------------
/**
 * A delegation hands somebody else your authority for a window.
 *
 * Both ends are required and the window is closed at both ends: an open-ended
 * delegation is indistinguishable from a permanent role grant that nobody
 * reviewed, which is the thing a delegation register exists to prevent.
 */
export const delegationCreateSchema = z
    .object({
    toUserId: z.string().uuid(),
    app: z.enum(APPS).nullable().optional(),
    businessUnitIds: z.array(z.string().uuid()).max(200).optional(),
    projectIds: z.array(z.string().uuid()).max(500).optional(),
    startsAt: dateOrInstant,
    endsAt: dateOrInstant,
    reason: z.string().trim().min(1, 'Say why authority is being delegated.').max(2000),
})
    // Dates compare as dates: a one-day delegation starts and ends on the same
    // date. The server checks the exact instants once it has the timezone.
    .refine((value) => (value.endsAt.length === 10 && value.startsAt.length === 10 ? value.endsAt >= value.startsAt : new Date(value.endsAt) > new Date(value.startsAt)), {
    message: 'A delegation must end after it begins.',
    path: ['endsAt'],
});
// ---------------------------------------------------------------------------
// Bulk role assignment (spec §13.1)
// ---------------------------------------------------------------------------
/**
 * A CSV upload of role grants.
 *
 * `dryRun` defaults to **true**, and that default is the point. A bulk grant is
 * the single fastest way to give the wrong people access to the wrong projects,
 * so the validated preview is what you get unless you ask for the opposite in
 * writing.
 *
 * Expected columns: `username,roleKey,businessUnitCodes,projectCodes,expiresAt`.
 */
export const bulkGrantSchema = z.object({
    csv: z.string().min(1, 'Paste or upload the CSV.').max(1_000_000),
    dryRun: z.boolean().default(true),
});
//# sourceMappingURL=rbac-admin.js.map